import { useCallback, useEffect, useMemo, useState } from "react";
import { PermitsTableProps } from "../components/PermitsTable";
import {
    checkoutPermitAsync,
    deletePermit as deletePermitAsync,
    getPaginatedBasePermitInfoByFilterAsync,
    getBaseUserInfoById,
    renewPermitAsync
} from "../apiClient";
import { showDangerDialog, showSuccessAlert } from "../common";
import PaginatedBasePermits from "../types/responses/PaginatedBasePermits";
import BaseWorkPermitInfo from "../types/BaseWorkPermitInfo";
import Swal from "sweetalert2";
import { permitPageUrl, versionedSequenceIdentifier } from "../common/permitUtils";
import { PermitType, WorkPermitSortProperty } from "../common/enums";
import BaseUserInfo from "../types/BaseUserInfo";
import useSignalR, { SignalRConnectionListeners } from "./useSignalR";
import WorkPermitFilter, { WorkPermitFilter_toURLSearchParams } from "../types/filters/WorkPermitFilter";

export default function usePermitsTable(props: PermitsTableProps) {
    const [paginatedContent, setPaginatedContent] = useState<PaginatedBasePermits>();
    const [isLoading, setIsLoading] = useState(true);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [mapModalPermit, setMapModalPermit] = useState<BaseWorkPermitInfo>();
    const [workPermitFilter, setWorkPermitFilter] = useState(props.defaultFilter);
    const [permitSeeker, setPermitSeeker] = useState<BaseUserInfo>()

    const signalRListeners: SignalRConnectionListeners = useMemo(() => ({
        onWorkPermitsUpdated: async () => await fetchPaginatedContentAsync(workPermitFilter)
    }), [workPermitFilter]);

    useSignalR(signalRListeners)

    const { resources } = props;

    async function applyFilterHandlerAsync(options?: { fromDate?: Date; toDate?: Date }) {
        if (requestInProgress) {
            return;
        }

        const filter: WorkPermitFilter = {
            ...workPermitFilter,
        }; 
        
        if (!!options && !!options.fromDate) {
            filter.fromDate = options.fromDate;
        }

        if (!!options && !!options.toDate) {
            filter.toDate = options.toDate;
        }

        await performRequest(() => fetchPaginatedContentAsync(filter));
    }

    useEffect(() => {
        async function fetchDataAsync() {

            const paginatedContentPromise = fetchPaginatedContentAsync(props.defaultFilter);
            const permitOwnerPromise = props.permitSeekerId ? fetchPermitOwnerAsync(props.permitSeekerId) : Promise.resolve();

            await Promise.all([paginatedContentPromise, permitOwnerPromise]);

            setIsLoading(false);
        }

        fetchDataAsync();
    }, []);

    async function performRequest<TReturnValue>(request: () => Promise<TReturnValue>): Promise<TReturnValue> {
        setRequestInProgress(true);
        const result = await request();
        setRequestInProgress(false);
        return result;
    }

    async function deleteButtonClickHandlerAsync(permit: BaseWorkPermitInfo) {
        const swalResult = await Swal.fire({
            icon: "warning",
            title: "Radera tillstånd",
            html: `
                    Är du säker på att du vill ta bort tillstånd
                    <span class="text-nowrap fw-bold">${versionedSequenceIdentifier(permit)}</span>?
                `,
            showCancelButton: true,
            confirmButtonText: "Ja",
            cancelButtonText: "Nej",
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33"
        });

        if (!swalResult.isConfirmed || requestInProgress) {
            return;
        }

        const deleteResult = await performRequest(() => deletePermitAsync(permit.id));

        if (deleteResult.failed) {
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("DeletePermitError"));
            return;
        }

        showSuccessAlert(resources.get("PermitDeleted"));

        await fetchPaginatedContentAsync(workPermitFilter);
    }

    async function checkoutButtonClickHandlerAsync(permit: BaseWorkPermitInfo) {
        if (requestInProgress) {
            return;
        }

        const checkoutResult = await performRequest(() => checkoutPermitAsync(permit.id, permit.permitName));

        if (checkoutResult.failed) {
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("CheckoutError"));
            return;
        }

        const lastPermitId = checkoutResult.value;

        let lastPermitUrl = permitPageUrl({
            permitType: permit.permitName,
            departmentId: permit.departmentId,
            workPermitId: lastPermitId
        });

        if (permit.permitName === PermitType.GeneralDepartmentPermit) {
            lastPermitUrl = permitPageUrl({
                permitType: permit.permitName,
                departmentId: permit.departmentId,
                sequenceIdentifier: permit.sequenceIdentifier
            });
        }

        window.location.href = lastPermitUrl;
    }

    async function fetchPaginatedContentAsync(filter: WorkPermitFilter) {
        syncLocation(filter);

        const permitsResult = await getPaginatedBasePermitInfoByFilterAsync(filter);

        if (permitsResult.failed) {
            console.error(permitsResult.error);
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("FetchPermitsError"));
            return;
        }

        setPaginatedContent(permitsResult.value);
    }

    async function fetchPermitOwnerAsync(permitOwnerId: string) {
        const permitOwnerResult = await getBaseUserInfoById(permitOwnerId);

        if (permitOwnerResult.failed) {
            console.error(permitOwnerResult.error);
            return;
        }

        setPermitSeeker(permitOwnerResult.value);
    }

    function syncLocation(filter: WorkPermitFilter) {
        const urlSearchParams = WorkPermitFilter_toURLSearchParams(filter);
        const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;

        window.history.replaceState({}, "", newUrl);
    }


    async function copyButtonClickHandlerAsync(permit: BaseWorkPermitInfo) {
        const newUrl = permitPageUrl({
            permitType: permit.permitName,
            copyOfDocumentReferenceId: permit.documentReferenceId
        });

        window.location.href = newUrl;
    }

    function mapButtonClickHandler(permit: BaseWorkPermitInfo) {
        setMapModalPermit({ ...permit });
    }

    async function hideMapModalHandler() {
        setMapModalPermit(undefined);
    }

    async function renewButtonClickHandlerAsync(permit: BaseWorkPermitInfo) {
        if (requestInProgress) {
            return;
        }

        const renewResult = await performRequest(() => renewPermitAsync(permit.id, permit.permitName));

        if (renewResult.failed) {
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("RefreshPageAndTryAgain"));
            return;
        }

        const newPermitDocumentId: string = renewResult.value;

        const newPermitUrl = permitPageUrl({
            permitType: permit.permitName,
            departmentId: permit.departmentId,
            documentReferenceId: newPermitDocumentId
        });

        window.location.href = newPermitUrl;
    }

    async function columnHeaderClickHandlerAsync(clickedSortProperty: WorkPermitSortProperty) {
        if (requestInProgress) {
            return;
        }

        const newFilter: WorkPermitFilter = {
            ...workPermitFilter,
            sortProperty: clickedSortProperty,
            isDescending:
                workPermitFilter.sortProperty === clickedSortProperty ? !workPermitFilter.isDescending : false,
            pageIndex: 1
        };

        setWorkPermitFilter(newFilter);
        await performRequest(() => fetchPaginatedContentAsync(newFilter));
    }

    async function pageIndexChangeHandlerAsync(newPageIndex: number) {
        if (requestInProgress) {
            return;
        }

        const newFilter: WorkPermitFilter = {
            ...workPermitFilter,
            pageIndex: newPageIndex
        };

        setWorkPermitFilter(newFilter);
        await performRequest(() => fetchPaginatedContentAsync(newFilter));
    }

    async function pageSizeChangeHandlerAsync(newPageSize: number) {
        if (requestInProgress) {
            return;
        }

        const newFilter: WorkPermitFilter = {
            ...workPermitFilter,
            pageSize: newPageSize,
            pageIndex: 1
        };

        setWorkPermitFilter(newFilter);
        await performRequest(() => fetchPaginatedContentAsync(newFilter));
    }

    const showMapModal = !!mapModalPermit;

    return {
        workPermitFilter,
        paginatedContent,
        isLoading,
        showMapModal,
        mapModalPermit,
        permitSeeker,
        deleteButtonClickHandlerAsync,
        checkoutButtonClickHandlerAsync,
        copyButtonClickHandlerAsync,
        mapButtonClickHandler,
        hideMapModalHandler,
        renewButtonClickHandlerAsync,
        columnHeaderClickHandlerAsync,
        applyFilterHandlerAsync,
        pageIndexChangeHandlerAsync,
        pageSizeChangeHandlerAsync,
        workPermitFilterChangeHandler: setWorkPermitFilter
    };
}
